import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useDispatch, useSelector } from 'react-redux';
import { addLike, copyTemplate } from '../../../../store/templates/actions';
import useTemplates from '../../use-templates';
import { resetPressedState } from '../../../../store/templates/templates';
import { history } from 'store/store';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { selectActiveTemplate } from '../../../../store/templates/selectors';
import { Values } from 'modules/template-editor/domain/interface/form/template';
import Modal from 'ud-ui/components/modal';
import { UDText } from 'ud-ui/components';
import { templatePresets, TemplatePresetsNameList } from 'modules/template-editor/domain/consts/form/template/template-presets';
import TemplateForm from 'modules/template-editor/ui/components/elements/forms/template-form';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { accessOptions, visibilityOptions } from 'modules/template-editor/domain/consts/form/template/options';

const PRESET: TemplatePresetsNameList = 'copy';
const POSTFIX = ' (копия)';

function useCopyModal(templateId: number) {
  const dispatch = useDispatch();
  const { info } = useOrganizer();
  const activeTemplate = useSelector(selectActiveTemplate);
  const { templates, isProcessed, isFinished, currentProcess } = useTemplates();
  const isAdmin = useIsAdmin();

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: Values) => {
      if (!info) {
        console.warn({ event: 'failed to copy template: info is undefined', info });
        return;
      }

      const { width, height, name } = values;
      const resolution = { width: +width, height: +height };
      dispatch(
        copyTemplate({
          ownerId: info.id,
          templateId,
          name: name.toString(),
          resolution,
          process: 'copy template',
        }),
      );
    },
    [dispatch, info, templateId],
  );

  const initialValue = {
    width: activeTemplate?.resolution.width?.toString() ?? '1920',
    height: activeTemplate?.resolution.height?.toString() ?? '1080',
    name: activeTemplate?.name ? activeTemplate?.name + POSTFIX : '',
    visibility: visibilityOptions.find((option) => option.value === activeTemplate?.visibility) ?? visibilityOptions[0],
    access: accessOptions.find((option) => option.value === activeTemplate?.access) ?? accessOptions[0],
  };

  useEffect(() => {
    if (!info) {
      return;
    }
    if (!isFinished) {
      return;
    }
    if (currentProcess !== 'copy template') {
      return;
    }
    close();
    dispatch(resetPressedState());
    dispatch(addLike({ templateId: templateId, userId: info.id }));
    history.push(Pages.TEMPLATE.INDEX, { isCoppedTemplate: true });
  }, [isProcessed, isFinished, close, dispatch, info, templateId, currentProcess]);

  const existingNames = useMemo(() => templates.map((template) => template.name.trim()), [templates]);

  return {
    modal: (
      <Modal show={isOpen} onClose={close}>
        <UDText type={'title-3'} className="mb-5">
          {templatePresets[PRESET].header}
        </UDText>
        <TemplateForm
          preset={PRESET}
          initialValues={initialValue}
          onSubmit={onSubmit}
          existingNames={existingNames}
          advancedAccess={isAdmin}
          isLoading={isProcessed}
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useCopyModal;
